import { useState, useEffect } from "react";
import { FaSearch } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { filterBySearch } from "../features/products/productSlice";
import Product from "../components/Product";
import { motion } from "framer-motion";
import AnimatedBackground from "../components/AnimatedBackground";

function Search() {
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const { filteredProducts } = useSelector((state) => state.products);

  useEffect(() => {
    if (search.trim()) {
      dispatch(filterBySearch(search));
    }
  }, [search, dispatch]);

  const onChange = (e) => {
    setSearch(e.target.value);
  };

  return (
    <div className="min-h-screen bg-[#0b0e14] py-20 px-4 md:px-8 selection:bg-cyan-500/30 relative overflow-hidden">
      <AnimatedBackground intensity={0.2} />
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-purple-600/5 rounded-full blur-[160px] pointer-events-none z-0" />
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[160px] pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto flex flex-col items-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-16 space-y-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest"
          >
            <motion.span
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-cyan-400 rounded-full"
            />
            Global Tactical Search v4.0
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
            className="text-4xl md:text-7xl font-black text-white uppercase italic tracking-tighter"
          >
            HARDWARE <span className="text-cyan-400">MATRIX</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-gray-500 font-bold uppercase tracking-[0.2em] text-xs max-w-lg mx-auto leading-relaxed"
          >
            Locate high-performance components within the GamerHub weaponized
            ecosystem.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="w-full max-w-3xl relative mb-20 group"
        >
          <motion.div
            className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-[2rem] blur opacity-20"
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          <input
            className="w-full p-6 pl-8 pr-16 text-xl font-bold bg-[#111827] border border-white/10 rounded-[2rem] text-white placeholder:text-gray-600 focus:outline-none focus:border-cyan-500/50 transition-all duration-300 shadow-2xl tracking-tight italic relative z-10"
            type="text"
            value={search}
            onChange={onChange}
            placeholder="Search by component name or matrix ID..."
          />
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute right-8 top-1/2 -translate-y-1/2 z-10"
          >
            <FaSearch className="text-cyan-400 text-2xl" />
          </motion.div>
        </motion.div>

        {search.trim() && (
          <div className="w-full animate-fadeIn">
            <div className="flex items-center gap-6 mb-12">
              <h2 className="text-sm font-black text-white uppercase tracking-[0.3em] italic">
                Results Indexed:{" "}
                <span className="text-cyan-400">{filteredProducts.length}</span>
              </h2>
              <div className="flex-1 h-px bg-white/5" />
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                >
                  <Product product={product} />
                </motion.div>
              ))}
            </motion.div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-32 bg-white/5 backdrop-blur-xl rounded-[3rem] border border-white/10 shadow-2xl">
                <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-white/5">
                  <FaSearch className="text-4xl text-gray-700" />
                </div>
                <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-4">
                  No Matches Located
                </h3>
                <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">
                  The matrix was unable to find hardware matching your query.
                  Try a different tactical string.
                </p>
              </div>
            )}
          </div>
        )}

        {!search.trim() && (
          <div className="text-center py-40 opacity-20">
            <FaSearch className="text-[12rem] mx-auto mb-12 text-gray-800 animate-pulse" />
            <p className="text-2xl font-black text-white uppercase tracking-[0.5em] italic">
              Awaiting Search Input
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;
